import type { KeyboardEvent } from 'react';
import { useEffect, useRef } from 'react';

import { TeamUserAvatar } from '@poseidon/components/Avatar';
import { cn } from '@poseidon/components/cn';
import { Button, Input } from '@poseidon/components/Input2';
import { Popup } from '@poseidon/components/Popup';
import { Arrows, Editor, General, Users } from '@poseidon/components/StrokeIcons';

import type { PaletteItem, PaletteSection } from '~/components/SuperadminSwitch/switchPalette.model';
import { flattenSections, moveActive } from '~/components/SuperadminSwitch/switchPalette.model';

export type SuperadminSwitchPalettePureProps = {
  query: string;
  onQueryChange: (q: string) => void;
  sections: PaletteSection[];
  activeId: string | null;
  onActiveIdChange: (id: string | null) => void;
  onSelect: (item: PaletteItem) => void;
  onClose: () => void;
  /** First search in flight with nothing to show yet. */
  isLoading: boolean;
  /** The row whose switch is running — shows "Switching…" and blocks other picks. */
  switchingId: string | null;
  showInactive: boolean;
  onToggleInactive: () => void;
  /** "⌘⇧K" on Mac, "Ctrl+Shift+K" elsewhere. */
  shortcutLabel: string;
};

/**
 * The superadmin switch palette (⌘⇧K): one search box over people, teams, recent switches and superadmin
 * pages. Data-free — the container owns queries, switching and recents.
 */
export function SuperadminSwitchPalettePure(props: SuperadminSwitchPalettePureProps) {
  const { sections, activeId, onActiveIdChange, onSelect, switchingId } = props;
  const items = flattenSections(sections);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // `autoFocus` alone loses to the Popup's portal mounting a frame later, so focus once it's on screen.
  useEffect(() => {
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, []);

  // Keep the active row in view while arrowing.
  useEffect(() => {
    if (activeId == null) return;
    listRef.current?.querySelector(`[data-palette-id="${CSS.escape(activeId)}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [activeId]);

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      onActiveIdChange(moveActive(items, activeId, e.key === 'ArrowDown' ? 1 : -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = items.find((it) => it.id === activeId) ?? items[0];
      if (item != null && switchingId == null) onSelect(item);
    } else if (e.key === 'Escape') {
      // The shared esc stack skips keys typed in inputs, so the search box closes the palette itself.
      e.preventDefault();
      props.onClose();
    }
  };

  return (
    <Popup onClose={props.onClose} className="w-[600px] max-w-[95vw] gap-0 p-0 text-left">
      <div data-testid="switch-palette" className="flex min-h-0 flex-col">
        <div className="border-b border-gray-200 p-3 dark:border-gray-700">
          <Input
            ref={inputRef}
            className="w-full"
            autoFocus
            size="md"
            icon={<General.SearchSm />}
            placeholder="Switch to a person or team, or jump to a superadmin page…"
            value={props.query}
            onChange={(e) => props.onQueryChange(e.target.value)}
            onKeyDown={onKeyDown}
            aria-label="Search people, teams and superadmin pages"
            data-testid="switch-palette-input"
          />
        </div>

        <div
          ref={listRef}
          className="max-h-[60vh] overflow-y-auto py-1"
          role="listbox"
          data-testid="switch-palette-list">
          {props.isLoading && items.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-gray-500" data-testid="switch-palette-loading">
              Searching…
            </p>
          )}
          {!props.isLoading && items.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-gray-500" data-testid="switch-palette-empty">
              {props.query.trim() === '' ? 'Type a name, email or team.' : `Nothing matches “${props.query.trim()}”.`}
            </p>
          )}
          {sections.map((section) => (
            <div key={section.key} data-testid={`switch-palette-section-${section.key}`}>
              {section.title !== '' && (
                <div className="px-4 pt-2 pb-1 text-[11px] font-semibold tracking-wide text-gray-500 uppercase dark:text-gray-400">
                  {section.title}
                </div>
              )}
              {section.items.map((item) => (
                <PaletteRow
                  key={item.id}
                  item={item}
                  active={item.id === activeId}
                  switching={item.id === switchingId}
                  disabled={switchingId != null && item.id !== switchingId}
                  onHover={() => onActiveIdChange(item.id)}
                  onPick={() => switchingId == null && onSelect(item)}
                />
              ))}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-gray-200 px-4 py-2 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
          <span>
            <kbd className="font-sans">↑↓</kbd> move · <kbd className="font-sans">↵</kbd> switch ·{' '}
            <kbd className="font-sans">esc</kbd> close · <kbd className="font-sans">{props.shortcutLabel}</kbd> anywhere
          </span>
          <Button
            size="2xs"
            text
            onClick={props.onToggleInactive}
            tooltip="Inactive = nothing sent in the last 30 days. Typing a search always includes them."
            data-testid="switch-palette-toggle-inactive">
            {props.showInactive ? 'Hide inactive' : 'Show inactive'}
          </Button>
        </div>
      </div>
    </Popup>
  );
}

function PaletteRow({
  item,
  active,
  switching,
  disabled,
  onHover,
  onPick,
}: {
  item: PaletteItem;
  active: boolean;
  switching: boolean;
  disabled: boolean;
  onHover: () => void;
  onPick: () => void;
}) {
  return (
    <div
      role="option"
      aria-selected={active}
      aria-disabled={disabled}
      data-palette-id={item.id}
      data-testid={`switch-palette-item-${item.id}`}
      onMouseMove={onHover}
      onClick={onPick}
      className={cn(
        'mx-1 flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700/60',
        active && 'bg-blue-50 hover:bg-blue-50 dark:bg-blue-900/30 dark:hover:bg-blue-900/30',
        disabled && 'cursor-default opacity-50',
      )}>
      <RowBody item={item} />
      <span className="ml-auto shrink-0 text-xs text-gray-400">
        {switching ? 'Switching…' : active ? <Arrows.ReverseLeft className="size-3.5" /> : null}
      </span>
    </div>
  );
}

function RowBody({ item }: { item: PaletteItem }) {
  if (item.kind === 'back') {
    return (
      <>
        <General.LogOut05 className="size-5 shrink-0 text-gray-500" />
        <div className="min-w-0">
          <div className="font-medium">Back to myself</div>
          <div className="truncate text-xs text-gray-500">Stop acting as someone else</div>
        </div>
      </>
    );
  }
  if (item.kind === 'page') {
    return (
      <>
        <Editor.Wand className="size-5 shrink-0 text-gray-500" />
        <div className="min-w-0">
          <div className="font-medium">{item.page.title}</div>
          <div className="truncate text-xs text-gray-500">{item.page.description}</div>
        </div>
      </>
    );
  }
  if (item.kind === 'team') {
    return (
      <>
        <Users.Users01 className="size-5 shrink-0 text-gray-500" />
        <div className="min-w-0">
          <div className="font-medium">{item.teamName}</div>
          <div className="truncate text-xs text-gray-500">
            Switch to {item.switchTo.name}
            {item.switchTo.isAdmin === true ? ' (admin)' : ''} · {item.memberCount}{' '}
            {item.memberCount === 1 ? 'seat' : 'seats'} found
          </div>
        </div>
      </>
    );
  }
  const t = item.target;
  const full = 'isActive' in t ? t : null;
  return (
    <>
      <TeamUserAvatar
        userTeam={{ profilePicture: t.profilePicture, userTeamName: t.name, userEmail: t.email }}
        className="size-6 shrink-0 text-3xs"
        shape="circle"
      />
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="truncate font-medium">{t.name}</span>
          {full?.isAdmin === true && <Badge>Admin</Badge>}
          {full?.isDfy === true && <Badge>DFY</Badge>}
          {full?.linkedin === 'error' && <Badge tone="red">LinkedIn error</Badge>}
          {full != null && !full.isActive && <Badge>Inactive</Badge>}
        </div>
        <div className="truncate text-xs text-gray-500">{[t.teamName, t.email].filter(Boolean).join(' · ')}</div>
      </div>
    </>
  );
}

function Badge({ children, tone = 'gray' }: { children: string; tone?: 'gray' | 'red' }) {
  return (
    <span
      className={cn(
        'shrink-0 rounded px-1 py-px text-[10px] font-medium',
        tone === 'red'
          ? 'bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-300'
          : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
      )}>
      {children}
    </span>
  );
}
